import { decisionSummaryMap } from "./constants.js";
import { RemoveYear, hasInErrorReason, formatTreatmentDetails, formatEpisodeDetails, getLatestChangeReason, getLatestChangeDate, } from "./helpers.js";
class LeaveSearchImpl {
    constructor() {
        this.format = (response, formatType) => {
            const searchResultMetadata = this.extractSearchMetadata(response);
            const LeaveSearch = this.processLeaves(response.leaves || [], formatType);
            return { searchResultMetadata, LeaveSearch };
        };
    }
    extractSearchMetadata(response) {
        return {
            startIndex: response.searchResultMetaData?.startIndex,
            totalResults: response.searchResultMetaData?.totalResults,
            itemsPerPage: response.searchResultMetaData?.itemsPerPage,
            timeTaken: response.searchResultMetaData?.resultMetadata?.timeTaken,
            links: response.searchResultMetaData?.resultMetaData?._links
        };
    }
    processLeaves(leaves, formatType) {
        const LeaveSearch = [];
        leaves.forEach((leave) => {
            if (!this.shouldProcessLeave(leave, formatType))
                return;
            const leaveData = this.extractLeaveData(leave);
            const periodEntries = this.processLeavePeriods(leave, leaveData);
            LeaveSearch.push(...periodEntries);
        });
        return LeaveSearch;
    }
    shouldProcessLeave(leave, formatType) {
        if (hasInErrorReason(leave.leaveCloseReason, leave.leaveCloseReasonDesc))
            return false;
        if (formatType === "Detailed" && (!leave.employee || !leave.leavePeriod))
            return false;
        if (formatType === "LeaveAsAccommodation" && leave.leaveReason !== "Leave as an Accommodation")
            return false;
        return true;
    }
    extractLeaveData(leave) {
        const addressData = this.getAddressData(leave);
        const digitalAddresses = this.getDigitalAddresses(leave);
        const { relatedClaims, relatedClaimLinkedInd } = this.getRelatedClaimsData(leave);
        return { leave, addressData, digitalAddresses, relatedClaims, relatedClaimLinkedInd };
    }
    getAddressData(leave) {
        const postalAddresses = leave.employee?.postalAddresses || [{ addressType: "" }];
        const addressTypeToFilter = leave.leaveSourceSystem === "FNEOS" ? "Home" : "PA";
        return postalAddresses
            .filter((address) => address.addressType === addressTypeToFilter)
            .reduce((latest, current) => new Date(String(current.beginDate)) > new Date(String(latest.beginDate)) ? current : latest, { beginDate: "01/01/1900" });
    }
    getDigitalAddresses(leave) {
        const digitalAddresses = leave.employee?.digitalAddresses || [];
        const homeDigitalAddress = digitalAddresses.find((address) => address.digitalAddressTypeCd === "HE");
        const workDigitalAddress = digitalAddresses.find((address) => address.digitalAddressTypeCd === "BE");
        const homeDigitalAddressFineos = digitalAddresses.find((address) => address.digitalAddressType?.toLowerCase() === "personal email");
        const workDigitalAddressFineos = digitalAddresses.find((address) => address.digitalAddressType?.toLowerCase() === "work email");
        return {
            digitalAddressText: homeDigitalAddress?.digitalAddressText || homeDigitalAddressFineos?.digitalAddressText || null,
            workDigitalAddressText: workDigitalAddress?.digitalAddressText || workDigitalAddressFineos?.digitalAddressText || null
        };
    }
    getRelatedClaimsData(leave) {
        const validRelatedClaims = (leave.relatedClaimDetails || []).filter((claim) => !hasInErrorReason(claim?.claimProcessingStage, claim?.customClaimStatusReason, claim?.claimStatusReason, claim?.claimStatusReasonDesc, claim?.claimStatusReasonCdDesc, claim?.customClaimStatusReasonCdDesc));
        const hasVisibleRelatedClaims = validRelatedClaims.length > 0;
        return {
            relatedClaims: hasVisibleRelatedClaims
                ? validRelatedClaims
                : [this.getDefaultRelatedClaim()],
            relatedClaimLinkedInd: hasVisibleRelatedClaims
                ? leave.relatedClaimLinkedInd
                : "N",
        };
    }
    getDefaultRelatedClaim() {
        return {
            claimId: "",
            claimCoverageTypeCd: "",
            claimStatus: "",
            disabilityBeginDt: "",
            policyAdminSystemCd: "",
            groupAgreementId: "",
            policyName: "",
            divisionId: "",
            divisionName: ""
        };
    }
    processLeavePeriods(leave, leaveData) {
        const allEntries = [];
        (leave.leavePeriod || []).forEach((period) => {
            if (hasInErrorReason(period.periodStatusReason, period.leavePeriodStatusReasonDesc)) {
                return;
            }
            const periodData = this.extractPeriodData(period);
            const periodEntries = this.processPeriodCombinations(leaveData, period, periodData);
            allEntries.push(...periodEntries);
        });
        return allEntries;
    }
    extractPeriodData(period) {
        return {
            protections: this.getProtections(period),
            absences: this.getAbsences(period),
            certificationDetails: this.getCertificationDetails(period),
            rtwRestrictions: this.getRtwRestrictions(period),
            classificationList: this.getClassificationList(period)
        };
    }
    getProtections(period) {
        return period.leavePeriodProtection?.length ? period.leavePeriodProtection : [this.getDefaultProtection()];
    }
    getDefaultProtection() {
        return {
            protectionId: "",
            protectionName: "",
            protectionDisplayName: "",
            protectionType: "",
            srcProtectionType: "",
            protectionStatusReasonDesc: "",
            protectionStatusDesc: "",
            availabilityStatusDesc: ""
        };
    }
    getAbsences(period) {
        if (period.intermittentAbsenceDetails?.length) {
            return [
                {
                    intermittentStatus: period.intermittentAbsenceDetails[0].intermittentStatus
                }
            ];
        }
        return [{ intermittentStatus: "" }];
    }
    getCertificationDetails(period) {
        const leavePeriodDocuments = period.leavePeriodDocuments?.length ? period.leavePeriodDocuments : [{ documentId: "" }];
        const certificationDetails = leavePeriodDocuments.flatMap((document) => document.documentLatestRevision?.certificationDetails || [{ certificationId: "" }]);
        return certificationDetails.length ? certificationDetails : [{ revisionId: "" }];
    }
    getRtwRestrictions(period) {
        const leavePeriodDocuments = period.leavePeriodDocuments?.length ? period.leavePeriodDocuments : [{ documentId: "" }];
        const documentAttributes = leavePeriodDocuments.flatMap((doc) => doc.documentLatestRevision?.documentAttributes || [{ attributeId: "" }]);
        const rtwRestrictionsFiltered = documentAttributes.filter((attr) => attr.attributeId === "1295");
        return rtwRestrictionsFiltered.length ? rtwRestrictionsFiltered : [{ attributeId: "" }];
    }
    getClassificationList(period) {
        const PeriodClassification = period.leavePeriodClassification || [];
        return PeriodClassification.length
            ? PeriodClassification.map((classification) => classification?.classificiationTypeDesc || "")
                .filter((desc) => desc !== "")
                .join(", ")
            : "";
    }
    generateCombinations(arrays) {
        if (arrays.length === 0)
            return [[]];
        if (arrays.length === 1)
            return arrays[0].map((item) => [item]);
        const [first, ...rest] = arrays;
        const restCombinations = this.generateCombinations(rest);
        const combinations = [];
        for (const item of first) {
            for (const combo of restCombinations) {
                combinations.push([item, ...combo]);
            }
        }
        return combinations;
    }
    getAccommodationDetails(leave) {
        const accommodations = leave.accommodationDetails?.length ? leave.accommodationDetails : [this.getDefaultAccommodation()];
        return accommodations.filter((accommodation) => !hasInErrorReason(accommodation?.decisionReasonDesc));
    }
    getDefaultAccommodation() {
        return {
            categoryDesc: "",
            typeDesc: "",
            decisionDesc: "",
            decisionReasonDesc: "",
            effectivenessDesc: "",
            consideredDisabilityInd: "",
            requiredInd: ""
        };
    }
    processPeriodCombinations(leaveData, period, periodData) {
        const { leave, addressData, digitalAddresses, relatedClaims, relatedClaimLinkedInd } = leaveData;
        const accommodationDetails = this.getAccommodationDetails(leave);
        const uniqueProtections = new Set();
        const collectionsToCompute = [
            periodData.protections,
            periodData.absences,
            periodData.certificationDetails,
            periodData.rtwRestrictions,
            relatedClaims,
            accommodationDetails
        ];
        const allCombinations = this.generateCombinations(collectionsToCompute);
        const allEntries = allCombinations
            .map((combo) => {
            const [protection, absence, certificationDetail, restriction, claim, accommodation] = combo;
            return this.createEntry(leave, period, protection, absence, certificationDetail, restriction, claim, accommodation, addressData, digitalAddresses, periodData.classificationList, uniqueProtections, relatedClaimLinkedInd);
        })
            .filter((entry) => entry !== null);
        return allEntries;
    }
    createEntry(leave, period, protection, absence, certificationDetail, restriction, claim, accommodation, addressData, digitalAddresses, classificationList, uniqueProtections, relatedClaimLinkedInd) {
        if (hasInErrorReason(leave.leaveCloseReason, leave.leaveCloseReasonDesc, period?.periodStatusReason, period?.leavePeriodStatusReasonDesc, accommodation?.decisionReasonDesc)) {
            return null;
        }
        const uniqueKey = claim.claimId
            ? `${protection?.protectionId}-${protection?.protectionName}-${protection?.protectionStatusDesc}-${claim?.claimId}`
            : `${protection?.protectionId}-${protection?.protectionName}-${protection?.protectionStatusDesc}`;
        const entry = this.buildBaseEntry(leave, period, absence, addressData, digitalAddresses, classificationList, certificationDetail, restriction, claim, accommodation, protection, relatedClaimLinkedInd);
        if (!uniqueProtections.has(uniqueKey)) {
            this.addProtectionDetails(entry, protection);
            uniqueProtections.add(uniqueKey);
        }
        return entry;
    }
    buildBaseEntry(leave, period, absence, addressData, digitalAddresses, classificationList, certificationDetail, restriction, claim, accommodation, protection, relatedClaimLinkedInd) {
        return {
            coverageType: leave.coverageType,
            age: leave.ageAtLeaveStart,
            dateOfBirth: leave.employee?.dateOfBirth,
            employeeId: leave.employee?.employeeId,
            digitalAddressText: digitalAddresses.digitalAddressText,
            workDigitalAddressText: digitalAddresses.workDigitalAddressText,
            earningType: leave.employee?.earningType,
            managerEmail: leave.employee?.managerEmail,
            workState: leave.employee?.employeeWorkStateCd,
            employerLocationName: leave.employee?.employerLocationName,
            firstName: leave.employee?.firstName,
            fullName: leave.employee?.name,
            firstHireDt: leave.employee?.firstHireDt,
            genderAtBirth: leave.employee?.genderAtBirth,
            jobTitle: leave.employee?.jobTitle,
            lastName: leave.employee?.lastName,
            tenureDays: leave.employee?.tenureDays,
            managerName: leave.employee?.managerName,
            taxIdentificationNumber: leave.employee?.taxIdentificationNumber,
            workScheduleType: leave.employee?.workScheduleTypeDesc,
            postalCode: addressData?.postalCode || null,
            jurisdictionCode: addressData?.jurisdictionCode || null,
            addressLineOne: addressData?.addressLineOne || null,
            addressLineTwo: addressData?.addressLineTwo || null,
            city: addressData?.city || null,
            intermittentStatus: this.getIntermittentStatus(leave, protection, absence),
            historicalLeaveInd: leave.historicalLeaveInd,
            lastDateWorked: leave.lastDateWorked,
            leaveId: leave.leaveId,
            leaveEndDt: leave.leaveEndDt,
            leaveCloseReason: leave.leaveCloseReason,
            fitForDutyReleaseDt: leave.fitForDutyReleaseDt,
            leavePeriodId: period.leavePeriodId,
            periodEndDt: period.periodEndDt,
            periodStatusReason: period.periodStatusReason,
            reducedScheduleHours: period.reducedScheduleHours,
            leavePeriodNbr: period.leavePeriodNbr,
            actualDeliveryDt: period?.actualDeliveryDt
                ? RemoveYear(period?.actualDeliveryDt)
                : null,
            leaveScheduleType: period.leaveScheduleType,
            periodStartDt: period.periodStartDt,
            periodStatus: period.periodStatus,
            reportGroupName: period.reportGroupName,
            reportGroupNumber: period.reportGroupNumber,
            estimatedDeliveryDt: period.estimatedDeliveryDt,
            employerReportGroupNumber: period.employerReportGroupNumber,
            leaveReason: leave.leaveReason,
            receivedDt: leave.leaveReceivedDt,
            leaveRequestDt: leave.leaveRequestDt,
            leaveStartDt: leave.leaveStartDt,
            leaveStatus: leave.leaveStatus,
            statusEffectiveDt: leave.leaveStatusEffectiveDt,
            leaveClosedDt: leave.leaveClosedDt,
            partTimeRTWDt: leave.rtwType === "Part Time" ? RemoveYear(leave.rtwDt) : null,
            fullTimeRTWDt: leave.rtwType === "Full Time" ? RemoveYear(leave.rtwDt) : null,
            rtwStatus: leave.rtwStatus,
            rtwType: leave.rtwType,
            rtwLastUpdateDt: leave.rtwLastUpdateDt,
            rtwRestriction: restriction.attributeValue,
            relatedClaimLinkedInd,
            hireDt: leave.employee?.hireDt || null,
            policyName: leave.relatedClaimDetails?.policyName,
            divisionName: leave.relatedClaimDetails?.divisionName,
            claimBenefitBeginDt: claim.claimBenefitBeginDt,
            claimApprovedThruDt: claim.claimApprovedThruDt,
            relatedStdClaimBenefitBeginDt: claim.claimBenefitBeginDt,
            relatedStdClaimApprovedThruDt: claim.claimApprovedThruDt,
            relatedDisabilityBeginDt: RemoveYear(claim.disabilityBeginDt),
            relatedStdClaimId: claim.claimId,
            relatedStdClaimStatus: claim.claimStatus,
            relatedStdClaimStatusReason: claim.customClaimStatusReason,
            relatedStdDivisionName: claim.divisionName,
            relatedStdDivisionId: claim.divisionId,
            relatedStdPolicyName: claim.policyName,
            relatedStdPolicyNumber: claim.groupAgreementId,
            relatedStdMaxDurationDate: claim.claimBenefitEndDt,
            relatedStdClaimDuration: claim.claimDuration,
            personDisplayName: leave.leaveSpecialist?.personDisplayName,
            classificiationTypeDesc: classificationList || null,
            certBeginDate: certificationDetail.beginDt,
            certEndDate: certificationDetail.endDt,
            treatmentDetail: formatTreatmentDetails(certificationDetail.treatmentDetails, leave.leaveSourceSystem || ""),
            episodeDetail: formatEpisodeDetails(certificationDetail.episodeDetails, leave.leaveSourceSystem || ""),
            categoryDesc: accommodation.categoryDesc,
            typeDesc: accommodation.typeDesc,
            decisionDesc: accommodation.decisionDesc,
            decisionReasonDesc: accommodation.decisionReasonDesc,
            effectivenessDesc: accommodation.effectivenessDesc,
            consideredDisabilityInd: accommodation.consideredDisabilityInd,
            requiredInd: accommodation.requiredInd,
            pregnancyRelatedInd: leave.pregnancyRelatedInd,
            leaveSubmitMethod: leave.leaveSubmissionSource,
            changeReason: getLatestChangeReason(leave),
            chgDate: getLatestChangeDate(leave),
        };
    }
    addProtectionDetails(entry, protection) {
        entry.protectionId = protection?.protectionId;
        entry.protectionStatusDesc = protection?.protectionStatusDesc;
        entry.protectionName = protection?.protectionName;
        entry.hoursAvailable = protection?.protectionHours?.hoursAvailable;
        entry.hoursTaken = protection?.protectionHours?.hoursTaken;
        entry.timeExhausted = protection?.protectionHours?.timeExhausted;
        entry.timeBankMethod = protection?.protectionHours?.timeBankMethod;
        entry.protectionType = protection?.protectionType;
        entry.availabilityStatusDesc = decisionSummaryMap[protection?.availabilityStatusDesc];
        entry.protectionStatusReasonDesc = protection?.protectionStatusReasonDesc;
    }
    getIntermittentStatus(leave, protection, absence) {
        return leave.leaveSourceSystem === "NVLNK"
            ? protection?.protectionStatusDesc
            : absence?.intermittentStatus;
    }
}
export const LeaveSearch = new LeaveSearchImpl();
